export const ELTOQUE_PAGE_URL = 'https://eltoque.com/en/tasas-de-cambio-cuba';
export const ELTOQUE_API_URL = 'https://tasas.eltoque.com/v1/trmi';
export const ELTOQUE_API_DOCS_URL = 'https://tasas.eltoque.com/docs/';
export const ELTOQUE_TERMS_URL =
  'https://eltoque.com/api-tasas-de-eltoque-terminos-y-condiciones-de-uso';
export const EXCHANGE_RATES_TIMEZONE = 'America/Havana';

/** Informal TRMI board — same set as elTOQUE mercado informal. */
export const TRACKED_CURRENCIES = [
  'USD', 'EUR', 'MLC', 'CAD', 'MXN', 'ZELLE', 'CLA',
] as const;

/** elTOQUE TRMI aliases → ISO / display codes (ECU = euro). */
export const ELTOQUE_CURRENCY_ALIASES: Record<string, string> = {
  ECU: 'EUR',
  USDT_TRC20: 'USDT',
  USDTTRC20: 'USDT',
};

/** Rolling lookback for `/v1/trmi` (API rejects ranges ≥ 24h). */
export const ELTOQUE_TRMI_LOOKBACK_HOURS = 23;

export type TrackedCurrency = typeof TRACKED_CURRENCIES[number];

export interface ExchangeRateQuote {
  code: string;
  cupPerUnit: number;
  change?: number | null;
}

export interface DailyExchangeRates {
  id: string;
  dayKey: string;
  source: 'eltoque-api' | 'eltoque-scrape' | 'manual';
  sourceUrl: string;
  fetchedAt: string;
  publishedAt?: string | null;
  rates: ExchangeRateQuote[];
  /** BCC segmento II (`tasaPublica`) — retail / población. */
  officialRates?: ExchangeRateQuote[] | null;
  /** BCC segmento III (`tasaEspecial`). */
  specialRates?: ExchangeRateQuote[] | null;
  /** Provenance for formal boards (never label non-BCC data as BCC). */
  formalSource?: 'bcc' | null;
}

export function exchangeDayKey(
  date = new Date(),
  timeZone = EXCHANGE_RATES_TIMEZONE,
): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

export function shouldRefreshToday(
  existing: Pick<DailyExchangeRates, 'dayKey'> | null | undefined,
  date = new Date(),
  timeZone = EXCHANGE_RATES_TIMEZONE,
): boolean {
  if (!existing?.dayKey) return true;
  return existing.dayKey !== exchangeDayKey(date, timeZone);
}

/** Client auto-refresh threshold when opening the rates board. */
export const EXCHANGE_RATES_STALE_MS = 24 * 60 * 60 * 1000;

export function isExchangeRatesStale(
  rates: Pick<DailyExchangeRates, 'fetchedAt' | 'publishedAt' | 'dayKey'> | null | undefined,
  now = new Date(),
  maxAgeMs = EXCHANGE_RATES_STALE_MS,
): boolean {
  if (!rates) return true;
  if (shouldRefreshToday(rates, now)) return true;
  const ts = Date.parse(rates.fetchedAt || rates.publishedAt || '');
  if (!Number.isFinite(ts)) return true;
  return now.getTime() - ts >= maxAgeMs;
}

/** Informal board badge: TRMI = official API, PORTAL = HTML scrape. */
export function exchangeRatesSourceBadge(
  source: DailyExchangeRates['source'] | null | undefined,
): 'TRMI' | 'PORTAL' | 'MANUAL' {
  if (source === 'eltoque-api') return 'TRMI';
  if (source === 'eltoque-scrape') return 'PORTAL';
  return 'MANUAL';
}

export function formatCupAmount(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return rounded.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function formatCupRate(value: number): string {
  return `${formatCupAmount(value)} CUP`;
}

function normalizeCode(code: string): string {
  const raw = String(code ?? '').trim().toUpperCase();
  return ELTOQUE_CURRENCY_ALIASES[raw] ?? raw;
}

export function currencyUnitLabel(code: string): string {
  return `1 ${normalizeCode(code)}`;
}

/** Wire format for elTOQUE `date_from` / `date_to` (`YYYY-MM-DD HH:MM:SS`, UTC). */
export function formatElToqueDateTime(date: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}-${p(date.getUTCMonth() + 1)}-${p(date.getUTCDate())} ${p(date.getUTCHours())}:${p(date.getUTCMinutes())}:${p(date.getUTCSeconds())}`;
}

/**
 * Official TRMI request URL for a Havana calendar day.
 * Prefer `date=YYYY-MM-DD` (matches common client usage); range is a fallback.
 * @see https://tasas.eltoque.com/docs/
 */
export function buildElToqueTrmiUrl(
  baseUrl = ELTOQUE_API_URL,
  now = new Date(),
  lookbackHours = ELTOQUE_TRMI_LOOKBACK_HOURS,
): string {
  const url = new URL(baseUrl);
  url.searchParams.set('date', exchangeDayKey(now));
  // Keep lookback helper available for callers that need an explicit window.
  void lookbackHours;
  return url.toString();
}

/** Rolling &lt;24h window ending at `now` (API rejects ranges ≥ 24 hours). */
export function buildElToqueTrmiRangeUrl(
  baseUrl = ELTOQUE_API_URL,
  now = new Date(),
  lookbackHours = ELTOQUE_TRMI_LOOKBACK_HOURS,
): string {
  const safeHours = Math.min(Math.max(lookbackHours, 1), ELTOQUE_TRMI_LOOKBACK_HOURS);
  const to = now;
  const from = new Date(now.getTime() - safeHours * 60 * 60 * 1000);
  const url = new URL(baseUrl);
  url.searchParams.set('date_from', formatElToqueDateTime(from));
  url.searchParams.set('date_to', formatElToqueDateTime(to));
  return url.toString();
}

function toNumber(value: unknown): number | null {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
}

function normalizeQuoteList(
  list: ExchangeRateQuote[] | null | undefined,
): ExchangeRateQuote[] | null {
  if (!Array.isArray(list)) return list ?? null;
  return list.flatMap((rate) => {
    const code = normalizeCode(rate.code);
    const cupPerUnit = toNumber(rate.cupPerUnit);
    if (!code || cupPerUnit === null) return [];
    const quote: ExchangeRateQuote = {
      code,
      cupPerUnit,
      change: rate.change ?? null,
    };
    return [quote];
  });
}

export function normalizeDailyRates(
  partial: Omit<DailyExchangeRates, 'id' | 'dayKey'> & { dayKey?: string },
  dayKey = exchangeDayKey(),
): DailyExchangeRates {
  const deduped = new Map<string, ExchangeRateQuote>();
  for (const rate of partial.rates ?? []) {
    const code = normalizeCode(rate.code);
    const cupPerUnit = toNumber(rate.cupPerUnit);
    if (!code || cupPerUnit === null) continue;
    deduped.set(code, {
      code,
      cupPerUnit,
      change: rate.change ?? null,
    });
  }

  const ordered = TRACKED_CURRENCIES
    .map((code) => deduped.get(code))
    .filter(Boolean) as ExchangeRateQuote[];

  for (const rate of deduped.values()) {
    if (!ordered.some((row) => row.code === rate.code)) ordered.push(rate);
  }

  return {
    id: 'latest',
    dayKey,
    source: partial.source,
    sourceUrl: partial.sourceUrl,
    fetchedAt: partial.fetchedAt,
    publishedAt: partial.publishedAt ?? null,
    rates: ordered,
    officialRates: normalizeQuoteList(partial.officialRates),
    specialRates: normalizeQuoteList(partial.specialRates),
    formalSource: partial.formalSource ?? null,
  };
}

export { parseElToqueApiPayload, parseElToqueHtml, parseElToquePageProps } from './elToqueParsers';
